import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
// @ts-expect-error No type declaration type exist
import { stringify as wktStringify } from 'wkt';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ExportModalReturnType } from '@connect-services/client/components/export-modal/export-modal.model';
import {
    DataExportModalComponent,
    ExportSelectionModeEnum,
} from '@connect-services/client/components/modals/data-export-modal/data-export-modal.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { NotificationEventEnum } from '@connect-services/client/models/notification.model';
import {
    Layer,
    LayerControllerGet200Response,
    LayerWithAttributes,
    MultiPolygon,
    Project,
    ProjectsService,
} from '@connect-services/client/sdk/api';
import { GeographicFilterService } from '@connect-services/client/services/geographic-filter.service';
import { LayerService } from '@connect-services/client/services/layer.service';
import {
    SelectedEntitiesByLayerId,
    SelectionStateService,
} from '@connect-services/client/services/map/selection/selection-state.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MapControlService } from '@connect-services/client/services/map-control.service';
import { NotificationService } from '@connect-services/client/services/notification.service';
import { ProjectEntityTableService } from '@connect-services/client/services/project-entity-table.service';
import { ZoneDelimiterService } from '@connect-services/client/services/zone-delimiter.service';
import DataExportNotification from '@connect-services/src/sdk/async/DataExportNotification';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export enum ExportFormat {
    GEOPACKAGE = 'GeoPackage',
    KML = 'KML',
    SHAPEFILE = 'SHP',
}

@Injectable({
    providedIn: 'root',
})
export class DataExportService extends MapBaseService {
    public readonly canConfirm = computed<boolean>(() => {
        return !!this.format() && (this.selectionStateService.hasSelectedEntities() || !!this.layerIds().size);
    });
    public readonly defaultProjection = signal<Project.ProjectionEnum>(Project.ProjectionEnum._2154);
    public readonly format = signal<ExportFormat>(ExportFormat.SHAPEFILE);
    public readonly isActive = signal(false);
    public readonly isGeographicZoneMode = computed<boolean>(
        () => this.selectionMode() !== ExportSelectionModeEnum.ENTITY_SELECTION,
    );
    public readonly isLoadingLayerNames = signal<boolean>(false);
    public readonly isMapFiltered = signal<boolean>(false);
    public readonly layerIds = signal<Set<string>>(new Set<string>());
    public readonly layerNameById = computed<Map<string, string>>(() => {
        const nameById = new Map<string, string>(this.layers().map((layer: Layer) => [layer.id, layer.name]));

        this.projectEntityTableService.layersByName()?.forEach((layers: LayerWithAttributes[], layerName: string) => {
            layers.forEach((layer: LayerWithAttributes) => {
                nameById.set(layer.id, layerName);
            });
        });

        return nameById;
    });
    public multiPolygon!: MultiPolygon;
    public readonly projection = signal<Project.ProjectionEnum>(Project.ProjectionEnum._2154);
    public readonly selectionMode = signal<ExportSelectionModeEnum | null>(null);

    private readonly layers = signal<Layer[]>([]);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly geographicFilterService = inject(GeographicFilterService);
    private readonly layerService = inject(LayerService);
    private readonly mapControlService = inject(MapControlService);
    private readonly matDialog = inject(MatDialog);
    private readonly notificationService = inject(NotificationService<DataExportNotification>);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEntityTableService = inject(ProjectEntityTableService);
    private readonly projectsService = inject(ProjectsService);
    private readonly selectionStateService = inject(SelectionStateService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly zoneDelimiterService = inject(ZoneDelimiterService);

    public constructor() {
        super();

        effect(() => {
            const defaultProjectProjection = this.projectBloc.value()?.projection;
            if (!defaultProjectProjection) {
                return;
            }

            this.projection.set(defaultProjectProjection);
            this.format.set(ExportFormat.SHAPEFILE);
        });

        effect(() => {
            if (!this.projectBloc.value()?.id) {
                return;
            }

            void this.loadLayerNames();
        });
    }

    public addLayerId(layerId: string): void {
        this.layerIds.update((previous: Set<string>) => new Set(previous).add(layerId));
    }

    public async exportData(): Promise<void> {
        this.snackBarService.openSuccessSnackBar('dataExportInProgress');

        const measurementToolControl = document.getElementById('measurement-tool-control') as HTMLButtonElement;
        if (measurementToolControl) {
            measurementToolControl.disabled = false;
        }

        this.resetControls();

        const isGeographicZoneMode: boolean = this.isGeographicZoneMode();
        const zoneLayerIds: string[] = Array.from(this.layerIds().values());
        const zoneDelimiter: string | undefined = isGeographicZoneMode
            ? await this.resolveExportZoneDelimiter()
            : undefined;

        try {
            await firstValueFrom(
                this.projectsService.projectControllerExportMap({
                    entityExportDto: {
                        entitiesPerLayerId: isGeographicZoneMode
                            ? undefined
                            : this.buildEntitiesPerLayerId(this.selectionStateService.selectedEntitiesByLayerId()),
                        format: this.format(),
                        layerIds: isGeographicZoneMode && zoneLayerIds.length ? zoneLayerIds : undefined,
                        projectId: this.projectBloc.value()!.id,
                        projection:
                            this.format() === ExportFormat.KML
                                ? Project.ProjectionEnum._4326
                                : (this.projection() ?? this.defaultProjection()),
                        zoneDelimiter,
                    },
                }),
            );
        } catch {
            this.snackBarService.openErrorSnackBar('dataExportFailed');

            return;
        }

        this.notificationService.register(NotificationEventEnum.DATA_EXPORT, 'dataExportSuccess', 'dataExportFailed');
    }

    public openShapeExportModal(exportSelectionMode: ExportSelectionModeEnum): void {
        const dialogRef = this.matDialog.open(DataExportModalComponent, {
            autoFocus: false,
            data: {
                exportSelectionMode: exportSelectionMode,
            },
            width: '40vw',
        });
        dialogRef.afterClosed().subscribe(async (result: ExportModalReturnType): Promise<void> => {
            if ([ModalResultEnum.CANCEL, ModalResultEnum.CLOSE].includes(result.type)) {
                return;
            }

            await this.exportData();
        });
    }

    public async refreshMapFilteredState(): Promise<void> {
        const zoneDelimiter: string | undefined = await this.zoneDelimiterService.getZoneDelimiterForSelection();
        this.isMapFiltered.set(!!zoneDelimiter);
    }

    public removeLayerId(layerId: string): void {
        this.layerIds.update((previous: Set<string>) => {
            const next = new Set(previous);
            next.delete(layerId);

            return next;
        });
    }

    public resetControls(): void {
        this.isActive.set(false);
        this.mapControlService.reloadSelectionModalControl({
            enabled: this.geographicFilterService.isGeographicFilterAllowedOnComponent(),
        });
    }

    private buildEntitiesPerLayerId(selectedEntitiesByLayerId: SelectedEntitiesByLayerId): Record<string, string[]> {
        return Object.fromEntries(
            Object.entries(selectedEntitiesByLayerId).map(([layerId, entities]) => [
                layerId,
                entities.map((entity) => entity.uuid),
            ]),
        );
    }

    private getMapBoundsZoneDelimiter(): string {
        this.multiPolygon = {
            coordinates: [[this.getMapBoundsCorners()]],
            type: MultiPolygon.TypeEnum.MULTI_POLYGON,
        };

        return wktStringify(this.multiPolygon);
    }

    private async loadLayerNames(): Promise<void> {
        this.isLoadingLayerNames.set(true);

        try {
            const allLayers: LayerControllerGet200Response = await this.layerService.getAllLayers({
                types: [Layer.TypeEnum.GEOM, Layer.TypeEnum.TABLE, Layer.TypeEnum.INTERSTANDARD],
            });

            this.layers.set(allLayers.items);
        } catch {
            this.layers.set([]);
        } finally {
            this.isLoadingLayerNames.set(false);
        }
    }

    private async resolveExportZoneDelimiter(): Promise<string> {
        return (await this.zoneDelimiterService.getZoneDelimiterForSelection()) ?? this.getMapBoundsZoneDelimiter();
    }
}
